import React, { useEffect, Suspense, useState } from 'react';
import { useRoutes, useLocation, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { useIdleTimer } from 'react-idle-timer';
import Hotjar from '@hotjar/browser';

import Notification from './components/UI/MessageBoxs/Notification';
import LoadingSpinner from './components/UI/LoadingSpinners/LoadingSpinner';
import LoadingApp from './components/UI/LoadingSpinners/LoadingApp';
import PageLoadingFallback from './components/UI/LoadingSpinners/PageLoadingFallback';
import ErrorMessage from './components/UI/MessageBoxs/ErrorMessage';
import SidePopupMessage from './components/UI/MessageBoxs/SidePopupMessage';
import ImpersonationBanner from './components/Login/ImpersonationBanner';
import DbUpdateWarning from './components/Login/DbUpdateWarning';
import useResetState from './hooks/use-reset';
import useFetchData from './hooks/use-fetch-data';
import { fetchUserPermissions } from './store/auth-actions';
import { getOrganizationFromSubdomain } from './functions/getOrganizationFromSubdomain';
import { subscribeToCrossTabAuthChanges } from './functions/crossTabAuthSync';
import { getRoutes } from './routes';
import { isMealPunchOnly } from './functions/routeMapper';

// List of organizations that use donor login (lowercase subdomain names)
const DONOR_ORGANIZATIONS = ['toroschushashiow'];

const App = () => {

  //#region hooks
  const { resetState } = useResetState()
 
  const dispatch = useDispatch()

  const location = useLocation(); 

  const notification = useSelector((state) => state.ui.notification)
  const errorMessage = useSelector((state) => state.ui.errorMessage)
  const loggedIn = useSelector((state) => state.auth.isLoggedIn)
  const portalUserLoggedIn = useSelector((state) => state.auth.portalUserLoggedIn)
  const hubUserLoggedIn = useSelector((state) => state.auth.hubUserLoggedIn)
  const donorLoggedIn = useSelector((state) => state.auth.donorLoggedIn)
  const parentLoggedIn = useSelector((state) => state.auth.parentLoggedIn)
  const supportLoggedIn = useSelector((state) => state.auth.supportLoggedIn)
  const allowHub = useSelector((state) => state.auth.hub)
  const visibleTabs = useSelector((state) => state.auth.permissions.VisibleTabs);
  const permissionsLoaded = useSelector((state) => state.auth.permissionsLoaded);
  
  
  
  const [trialExpiration, setTrialExpiration] = useState(null)
  //#endregion
  
  useEffect(() => {
    if (loggedIn) {
            dispatch(fetchUserPermissions())
    }

},[loggedIn, dispatch])


useFetchData()

  useEffect(() => {
    if (localStorage.getItem('inTrial') && localStorage.getItem('inTrial') === 'True') {
      var now = new Date()
      var endDate = new Date(localStorage.getItem('trialEndDate'))
      const utc1 = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
      const utc2 = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

      var days = Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));
      setTrialExpiration(days)
    }
    else {
      setTrialExpiration(null)
    }
  }, [loggedIn])

  useEffect(()=>{
    const siteId = 3737662;
    const hotjarVersion = 6;

    Hotjar.init(siteId, hotjarVersion);
  },[])



  //#endregion

  
  const handleOnIdle = () => {
    // The meal punch kiosk stands unattended between meals.
    if (window.location.pathname === '/meal-punch') return
    logoutFromAll()
  }

  const logoutFromAll = () => {
    resetState()
  }


  useIdleTimer({
    timeout: 1800000,
    onIdle: handleOnIdle,
    debounce: 500
  })

  const hasSession = loggedIn || portalUserLoggedIn || hubUserLoggedIn || parentLoggedIn || donorLoggedIn || supportLoggedIn
  useEffect(() => {
    return subscribeToCrossTabAuthChanges({ hasSession: () => hasSession })
  }, [hasSession])

// Get route configuration
  const organization = getOrganizationFromSubdomain();
  const isDonorOrg = DONOR_ORGANIZATIONS.includes(organization);

  const routes = getRoutes({
    loggedIn,
    portalUserLoggedIn,
    hubUserLoggedIn,
    donorLoggedIn,
    supportLoggedIn,
    allowHub,
    visibleTabs,
    location,
    isDonorOrg
  });

  const routesElement = useRoutes(routes);
  
  // Show full-screen loading while permissions are being fetched
  if (!permissionsLoaded && loggedIn) {
    return <LoadingApp />;
  }

  if (loggedIn && isMealPunchOnly(visibleTabs) && location.pathname !== '/meal-punch') {
    return <Navigate to="/meal-punch" replace />
  }

  return (
    <React.Fragment>
      <ImpersonationBanner />
      {trialExpiration !== null && <p className='trial'>{trialExpiration === 1 ? '1 day left on trial!' : trialExpiration === 0 ? 'Last day on trial!' : trialExpiration + ' days left on trial!'}<br /><a className='trial-a' href='https://advanceddata.org/contact-us/' target='_blank' rel='nooepener noreferrer'>Contact sales</a> now to activate!</p>}
      {errorMessage && <ErrorMessage />}
      {notification && <Notification message={{ message: notification.message, status: notification.status, title: notification.title }} onClose={() => window.location.reload()} secondButton={notification.secondButton} onSecondButtonClick={notification.onSecondButtonClick} />}
      <SidePopupMessage/>
      <DbUpdateWarning />
      <Suspense fallback={<PageLoadingFallback />}>
        {routesElement}
      </Suspense>
    </React.Fragment>
  )
}

export default App;
